<template>
  <div class="artist">
    <div
      class="artist__background"
      :style="{
        backgroundImage: `url(${artist.header_image})`,
      }"
    ></div>
    <div class="artist__content">
      <ArtistDetailsBox :artist="artist" />
      <SpecificArtistTitleBox
        v-if="hasArtworks"
        :artist="artist"
        :sortData="sortData"
        :cardData="cardData"
        :filters="filters"
        :search="search"
        :openFilters="openFilters"
        @selectSort="selectSort"
        @selectFilter="selectFilter"
        @applyFilters="refreshSelectedFilters"
        @clearFilters="refreshSelectedFilters(true)"
        @changeSearch="changeSearch"
        @update:openFilters="openFilters = $event"
      />
      <SpecificArtistArtworksList
        v-if="hasArtworks && relatedArtworks.length"
        :artworks="relatedArtworks"
      />
      <h6 v-if="hasArtworks && !relatedArtworks.length" class="artist__empty">No artworks</h6>
      <div v-if="hasArtworks && hasMoreArtworksPages" class="artist__button-box">
        <BaseButton
          class="button button--xs button--white"
          text="LOAD MORE ARTWORKS"
          @click.native="loadMoreArtworks"
        />
      </div>
      <SpecificArtistPublicationsList
        v-if="hasPublications"
        :relatedPublications="relatedPublications"
      />
      <ArtistCVHighlights v-if="artist.past_shows_and_fair_booths" :artist="artist" />
    </div>
  </div>
</template>

<script>
import { mapMutations, mapActions, mapGetters } from "vuex";
import BaseButton from "@/components/buttons/BaseButton.vue";
import ArtistDetailsBox from "@/views/public/artists/components/ArtistDetailsBox.vue";
import SpecificArtistTitleBox from "@/views/public/artists/components/SpecificArtistTitleBox.vue";
import SpecificArtistArtworksList from "@/views/public/artists/components/SpecificArtistArtworksList.vue";
import SpecificArtistPublicationsList from "@/views/public/artists/components/SpecificArtistPublicationsList.vue";
import ArtistCVHighlights from "@/views/public/artists/components/ArtistCVHighlights.vue";
import useSeoHead from "@/composables/useSeoHead";

export default {
  components: {
    BaseButton,
    ArtistDetailsBox,
    SpecificArtistTitleBox,
    SpecificArtistArtworksList,
    SpecificArtistPublicationsList,
    ArtistCVHighlights,
  },

  data() {
    return {
      sortData: [
        { id: "availability_a_z", title: "Availability (A-Z)" }, // title_a_z
        { id: "availability_z_a", title: "Availability (Z-A)" }, // title_z_a
      ],
      cardData: {
        page: 1,
        sort: "availability_a_z", // title_a_z
        art_types: "",
        materials: "",
        price_start: "",
        price_end: "",
        periods: "",
        colors: "",
        regions: "",
        availabilities: "",
        width_start: "",
        width_end: "",
        height_start: "",
        height_end: "",
        depth_start: "",
        depth_end: "",
      },
      filterBlock: {
        list: "art_types,materials,price,periods,colors,regions,availabilities,sizes",
        type: "collection",
      },
      openFilters: false,
      publicationsPage: 1,
      exhibitionsPage: 1,
    };
  },

  head() {
    const name = this.artist?.name ?? "Artist browngrotta arts";
    const description = this.artist?.description ?? "Description Artist browngrotta arts";
    const image = this.artist?.image ?? "/img/icons/favicon-500x500.png";

    return useSeoHead({
      title: name,
      description: `${name} - ${description}`,
      image,
      routePath: this.$route.path,
    });
  },

  computed: {
    ...mapGetters("publicapi/artists", {
      artist: "getArtist",
      hasArtworks: "getHasArtworks",
      hasPublications: "getHasPublications",
      relatedArtworks: "getRelatedArtworks",
      hasMoreArtworksPages: "getHasMoreArtworksPages",
      relatedPublications: "getRelatedPublications",
      hasMorePublicationsPages: "getHasMorePublicationsPages",
    }),
    ...mapGetters("publicapi/filters", {
      filters: "getFilters",
      search: "getSearch",
      selectedFilters: "getSelectedFilters",
    }),
  },

  async created() {
    await this.apiGetSpecificArtist({ slug: this.$route.params.slug });
    this.$emit("updateHead");
    this.apiGetFiltersBlock({ ...this.filterBlock, search: this.search, isFirst: true });
    this.apiGetRelatedArtworks({ ...this.cardData, slug: this.artist.slug });
    await this.apiGetRelatedPublications({ slug: this.artist.slug });

    const breadcrumbs = [
      {
        title: "Home",
        to: "/",
      },
      {
        title: "Artists",
        to: "/artists",
        clickable: true,
      },
      {
        title: `${this.artist.name}`,
        to: `/artists/${this.artist.slug}`,
        clickable: false,
      },
    ];
    this.setBreadcrumbs(breadcrumbs);

    this.setShareData({
      title: this.artist.name,
      description: this.artist.biography,
      image: this.artist.header_image,
    });
  },

  methods: {
    ...mapActions("publicapi/artists", [
      "apiGetSpecificArtist",
      "apiGetRelatedArtworks",
      "apiGetRelatedPublications",
      "apiGetRelatedExhibitions",
    ]),
    ...mapMutations("publicapi/share", ["setShareData"]),
    ...mapActions("publicapi/filters", ["apiGetFiltersBlock"]),
    ...mapMutations("publicapi/filters", ["updateFilters", "updateSelectedFilters", "clear"]),
    ...mapMutations(["setBreadcrumbs"]),

    async loadMoreArtworks() {
      if (this.hasMoreArtworksPages) {
        let data = { ...this.cardData, page: this.cardData.page + 1 };
        let success = await this.apiGetRelatedArtworks({ ...data, slug: this.artist.slug });
        if (success) {
          this.cardData = data;
        }
      }
    },
    async loadMorePublications() {
      if (this.hasMorePublicationsPages) {
        let success = await this.apiGetRelatedPublications({
          slug: this.artist.slug,
          page: this.publicationsPage++,
        });
        if (success) {
          this.publicationsPage++;
        }
      }
    },
    async selectSort({ id }) {
      let data = { ...this.cardData, page: 1, sort: id };
      let success = await this.apiGetRelatedArtworks({ ...data, slug: this.artist.slug });
      if (success) {
        this.cardData = { ...data };
      }
    },
    selectFilter(val) {
      this.updateFilters(val);
      if (val.apply) {
        this.refreshSelectedFilters();
      }
    },
    async refreshSelectedFilters(clear) {
      if (clear) {
        await this.clear();
      }
      await this.updateSelectedFilters();
      this.filterTable();
    },
    async filterTable() {
      let data = { ...this.cardData, page: 1 };
      this.selectedFilters.forEach(el => {
        const values = ["price", "width", "height", "depth"];

        if (values.includes(el.key)) {
          data[`${el.key}_start`] = "";
          data[`${el.key}_end`] = "";
          el.items.forEach(i => {
            data[i.key] = i.val;
          });
        } else {
          let filters = el.items.map(i => {
            return i.id;
          });
          data[el.key] = filters.join(",");
        }
      });
      let success = await this.apiGetRelatedArtworks({ ...data, slug: this.artist.slug });
      if (success) {
        this.cardData = data;
      }
    },
    async changeSearch(search) {
      await this.apiGetFiltersBlock({ ...this.filterBlock, search });
    },
  },
};
</script>

<style lang="scss" scoped>
* {
  color: $white;
  font-size: 1.8rem;
  line-height: 2.4rem;
  @media screen and (max-width: $xs) {
    font-size: 1.6rem;
    line-height: 2.5rem;
  }
}

.artist {
  display: flex;

  &__background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    @media screen and (max-width: $xs) {
      height: 33.5rem;
    }
    &::before {
      content: "";
      position: absolute;
      width: 100%;
      height: 100%;
      background: rgba($color: $lightBlack, $alpha: 0.45);
      z-index: 1;
    }
    &::after {
      content: "";
      bottom: -1px;
      position: absolute;
      width: 100%;
      height: 50%;
      background: linear-gradient(
        0deg,
        rgba($color: $lightBlack, $alpha: 1) 0%,
        rgba($color: $lightBlack, $alpha: 0) 100%
      );
      z-index: 1;
    }
  }

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: calc(100vh - 35rem) 10.5rem 24rem;
    z-index: 2;
    @media screen and (max-width: $xs) {
      padding: 24.5rem 2rem 11rem;
    }
  }

  &__empty {
    @include emptyMessage();
  }

  &__button-box {
    @include centeredButtonBox();
  }
}
</style>

